import React from 'react';
import { SignUp } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useSelector } from 'react-redux';
import useAuthRedirect from '@common/hooks/useAuthRedirect';
import routes from '../../../common/routes/index';
import styles from './ClerkSignUp.module.css';
import { selectColorScheme } from '@src/features/interface/interfaceSlice';

/**
 * Clerk-based SignUp component
 * Wraps Clerk's SignUp component with application styling and theme
 * Maintains compatibility with existing authentication flow
 * Note: Organization field collection will be added in Phase 3
 */
const ClerkSignUp = (): React.JSX.Element | null => {
  const { isSignedIn, isLoaded } = useAuth();
  const { getRedirectUrl } = useAuthRedirect();
  const colorScheme = useSelector(selectColorScheme);

  // Wait for auth state to be loaded
  if (!isLoaded) {
    return null;
  }

  // Redirect if already signed in
  if (isSignedIn) {
    const redirectTo = getRedirectUrl();
    return <Navigate to={redirectTo} replace />;
  }

  const redirectAfterSignUp = getRedirectUrl();

  return (
    <div className={styles.SignUpContainer}>
      <SignUp 
        signInUrl={routes.auth.getSignInRoute()}
        forceRedirectUrl={redirectAfterSignUp}
        routing="hash"
        appearance={{
          captcha: {
            theme: colorScheme
          },
          elements: {
            rootBox: styles.ClerkRootBox,
            headerTitle: styles.ClerkHeaderTitle,
            headerSubtitle: styles.ClerkHeaderSubtitle,
            socialButtonsBlockButton: { display: 'none' },
            socialButtonsProviderIcon: { display: 'none' },
            dividerRow: { display: 'none' },
            formButtonPrimary: styles.ClerkSubmitButton,
            formFieldInput: styles.ClerkInput,
            formFieldLabel: styles.ClerkLabel,
            identityPreviewText: styles.ClerkIdentityText,
            formResendCodeLink: styles.ClerkLink,
            footerActionLink: styles.ClerkFooterLink,
            formFieldInputShowPasswordButton: styles.ClerkShowPasswordButton,
          },
        }}
      />
    </div>
  );
};

export default ClerkSignUp;
